import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

export default class extends Controller {
  static targets = ["alertModal", "alertMessage", "toggleLabel", "toggleCheckbox", "browserDisabledBanner", "bannerDetails", "bannerExpandButton", "pwaRequiredHint"]

  static values = {
    vapidPublicKey: String,
    createUrl: String,
    unsubscribeUrl: String,
    unsubscribeAllUrl: String,
    browserDisableNotificationsUrl: String,
    dismissBannerUrl: String,
    hasPushSubscriptions: Boolean
  }

  connect() {
    this.checkSubscriptionSync()
    this.checkPwaRequired()
  }

  // Show a hint if push notifications aren't available in this context
  // (e.g. regular Safari on iOS, where push only works in the installed PWA).
  // navigator.standalone is an iOS Safari-specific property:
  //   undefined → not iOS Safari
  //   false    → iOS Safari in regular browser (push won't work)
  //   true     → iOS Safari in standalone PWA (push works)
  checkPwaRequired() {
    if (!this.hasPwaRequiredHintTarget) return

    if (window.navigator.standalone === false) {
      this.pwaRequiredHintTarget.classList.remove("hidden")

      // Disable the toggle since it can't work
      if (this.hasToggleCheckboxTarget) {
        this.toggleCheckboxTarget.disabled = true
      }
      if (this.hasToggleLabelTarget) {
        this.toggleLabelTarget.classList.add("opacity-50", "pointer-events-none")
      }
    }
  }

  get vapidKey() {
    return this.vapidPublicKeyValue || document.querySelector('meta[name="vapid-public-key"]')?.content
  }

  // Checks whether the browser push subscription matches the app state.
  // Only treats it as a mismatch when Notification.permission === "denied",
  // meaning the user explicitly blocked notifications in this browser.
  // A browser that was never asked ("default") or has permission but no subscription
  // does NOT trigger a mismatch — this prevents wiping subscriptions from other browsers.
  async checkSubscriptionSync() {
    if (!this.hasPushSubscriptionsValue) return

    try {
      const registration = await navigator.serviceWorker?.ready
      if (!registration) return

      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        // Browser has an active subscription — in sync
        this.clearSyncGuard()
        if (this.hasToggleCheckboxTarget) {
          this.toggleCheckboxTarget.checked = true
        }
      } else if (Notification.permission === "denied") {
        // User explicitly blocked notifications in this browser — real mismatch
        this.element.dataset.pushSubscriptionHasPushSubscriptionsValue = "false"
        if (this.hasToggleCheckboxTarget) {
          this.toggleCheckboxTarget.checked = false
        }
        if (this.hasBrowserDisabledBannerTarget) {
          this.browserDisabledBannerTarget.classList.remove("hidden")
        }

        // POST to server to destroy this browser's subscription and set session flag (layout banner only)
        if (this.browserDisableNotificationsUrlValue) {
          const lastSync = this.getSyncGuard()
          if (!lastSync || Date.now() - lastSync >= 60000) {
            const storedEndpoint = (() => { try { return localStorage.getItem("push_endpoint") } catch (_e) { return null } })()
            const response = await post(this.browserDisableNotificationsUrlValue, {
              body: JSON.stringify({ endpoint: storedEndpoint }),
              contentType: "application/json",
              headers: { "Accept": "application/json" }
            })
            if (response.ok) {
              this.setSyncGuard()
              try { localStorage.removeItem("push_endpoint") } catch (_e) { /* ignore */ }
            }
          }
        }
      } else {
        // Permission is "default" or "granted" but no subscription in this browser.
        // Another browser may have the subscription — don't destroy anything.
        // Just update the toggle for this browser's state.
        if (this.hasToggleCheckboxTarget) {
          this.toggleCheckboxTarget.checked = false
        }
      }
    } catch (_err) {
      // Ignore sync errors
    }
  }

  // Dismiss the persistent banner and clear the server-side session flag
  dismissBanner() {
    this.element.remove()
    if (this.dismissBannerUrlValue) {
      post(this.dismissBannerUrlValue, {
        body: JSON.stringify({}),
        contentType: "application/json",
        headers: { "Accept": "application/json" }
      })
    }

    // Let lower-priority banners (e.g. PWA install) know they can show now
    document.dispatchEvent(new CustomEvent("banner:dismissed"))
  }

  getSyncGuard() {
    const v = sessionStorage.getItem("push_sync_at")
    return v ? parseInt(v, 10) : null
  }

  setSyncGuard() {
    sessionStorage.setItem("push_sync_at", String(Date.now()))
  }

  clearSyncGuard() {
    sessionStorage.removeItem("push_sync_at")
  }

  toggleBannerDetails() {
    if (!this.hasBannerDetailsTarget || !this.hasBannerExpandButtonTarget) return
    const expanded = this.bannerDetailsTarget.classList.toggle("hidden")
    this.bannerExpandButtonTarget.textContent = expanded
      ? this.bannerExpandButtonTarget.dataset.expandText
      : this.bannerExpandButtonTarget.dataset.collapseText
  }

  async toggle(_event) {
    // change event fires after checkbox toggles; checked = desired ON, unchecked = desired OFF
    const wantsEnabled = this.hasToggleCheckboxTarget && this.toggleCheckboxTarget.checked

    if (this.hasToggleLabelTarget && this.toggleLabelTarget.classList.contains("pointer-events-none")) return
    if (this.hasToggleLabelTarget) {
      this.toggleLabelTarget.classList.add("pointer-events-none", "opacity-50")
    }

    try {
      if (wantsEnabled) {
        const ok = await this.subscribe()
        if (!ok && this.hasToggleCheckboxTarget) {
          this.toggleCheckboxTarget.checked = false
        }
      } else {
        await this.unsubscribe()
      }
    } catch (err) {
      console.error("Push subscription error:", err)
      this.showError(this.errorMessage("Something went wrong. Please try again."))
      if (wantsEnabled && this.hasToggleCheckboxTarget) {
        this.toggleCheckboxTarget.checked = false
      }
    } finally {
      if (this.hasToggleLabelTarget) {
        this.toggleLabelTarget.classList.remove("pointer-events-none", "opacity-50")
      }
    }
  }

  errorMessage(key) {
    const messages = {
      "permission_denied": "Notifications are blocked for this site. To enable them, open your browser settings (click the lock or info icon in the address bar), find Notifications, and allow them for this site. Then refresh the page and try again.",
      "vapid_missing": "Push notifications are not configured. Please contact support.",
      "service_worker": "Please ensure you're using HTTPS and try again.",
      "service_worker_timeout": "The app is still loading. Wait a few seconds and try again.",
      "Something went wrong. Please try again.": "Something went wrong. Please try again."
    }
    return messages[key] || key
  }

  showError(message) {
    if (this.hasAlertModalTarget && this.hasAlertMessageTarget) {
      this.alertMessageTarget.textContent = message
      this.alertModalTarget.classList.remove("hidden")
      this.alertModalTarget.classList.add("flex")
    } else if (typeof window !== "undefined" && window.alert) {
      window.alert(message)
    }
  }

  hideAlert() {
    if (this.hasAlertModalTarget) {
      this.alertModalTarget.classList.add("hidden")
      this.alertModalTarget.classList.remove("flex")
    }
  }

  hideAlertOnBackdrop(event) {
    if (event.target === this.alertModalTarget) {
      this.hideAlert()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // Returns a ServiceWorkerRegistration with an active worker (for PushManager). Uses
  // register() then polls for .active, so we don't depend on .ready (which requires a
  // controlling worker and can hang on iOS PWA). Resolves with null on timeout.
  async getPushRegistration(maxMs) {
    try {
      const reg = await navigator.serviceWorker.register("/service-worker", { scope: "/" })
      const deadline = Date.now() + maxMs
      while (Date.now() < deadline) {
        if (reg.active) return reg
        await new Promise((r) => setTimeout(r, 150))
      }
      return null
    } catch (_err) {
      return null
    }
  }

  async subscribe() {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      this.showError(this.errorMessage("service_worker"))
      return false
    }

    const vapidKey = this.vapidKey
    if (!vapidKey) {
      this.showError(this.errorMessage("vapid_missing"))
      return false
    }

    if (!this.createUrlValue) {
      this.showError(this.errorMessage("vapid_missing"))
      return false
    }

    const permission = await Notification.requestPermission()
    if (permission !== "granted") {
      this.showError(this.errorMessage("permission_denied"))
      return false
    }

    // Get a registration we can use for PushManager. Prefer .ready; on iOS PWA it may
    // never resolve, so fall back to the registration from register() once it's active.
    const registration = await this.getPushRegistration(20000)
    if (!registration) {
      this.showError(this.errorMessage("service_worker_timeout"))
      return false
    }
    let subscription = await registration.pushManager.getSubscription()

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidKey)
      })
    }

    if (subscription) {
      try {
        await this.sendSubscriptionToServer(subscription)
        this.clearSyncGuard()
        this.element.dataset.pushSubscriptionHasPushSubscriptionsValue = "true"
        // Remember this browser's endpoint so we can identify it later if permission is revoked
        try { localStorage.setItem("push_endpoint", subscription.endpoint) } catch (_e) { /* ignore */ }
        return true
      } catch (err) {
        console.error("Failed to save subscription:", err)
        throw err
      }
    }
    return false
  }

  async unsubscribe() {
    const registration = await this.getPushRegistration(20000)
    if (!registration) {
      this.showError(this.errorMessage("service_worker_timeout"))
      return
    }
    const subscription = await registration.pushManager.getSubscription()

    if (subscription) {
      const endpoint = subscription.endpoint
      try {
        await subscription.unsubscribe()
      } catch {
        // Browser unsubscribe may fail; still sync server
      }
      try { localStorage.removeItem("push_endpoint") } catch (_e) { /* ignore */ }
      if (this.unsubscribeUrlValue) {
        const response = await post(this.unsubscribeUrlValue, {
          body: JSON.stringify({ endpoint }),
          contentType: "application/json",
          headers: { "Accept": "application/json" }
        })
        if (response.ok) {
          this.element.dataset.pushSubscriptionHasPushSubscriptionsValue = "false"
        }
      }
    } else if (this.unsubscribeAllUrlValue) {
      const response = await post(this.unsubscribeAllUrlValue, {
        body: JSON.stringify({}),
        contentType: "application/json",
        headers: { "Accept": "application/json" }
      })
      if (response.ok) {
        this.element.dataset.pushSubscriptionHasPushSubscriptionsValue = "false"
      }
    }
  }

  async sendSubscriptionToServer(subscription) {
    const json = subscription.toJSON()
    const body = JSON.stringify({
      push_subscription: {
        endpoint: json.endpoint,
        p256dh_key: json.keys?.p256dh,
        auth_key: json.keys?.auth
      }
    })

    await post(this.createUrlValue, {
      body,
      contentType: "application/json",
      headers: { "Accept": "application/json" }
    })
  }

  urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const rawData = atob(base64)
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)))
  }
}
